import React from 'react';
import {
  toUpper,
  forEach,
  reduce,
  find,
  each,
  isEmpty,
  sortBy,
  filter,
  maxBy,
  isString,
} from 'lodash';
import humps from 'humps';
import { format } from 'date-fns';
import { APPLICANT_TYPES, DATE_FORMAT_SHORT } from '../../constants';
import { getCarManufacturerModels } from '../../redux/modules/carMarks';
import ACCIDENT_PARTICIPANT_TYPES, {
  INSURED_TYPES,
} from '../../containers/CascoCompensationContainer/options';
import { getCarCityVillages } from '../../redux/modules/cmtplAdresses';
import { getCityVillages, getStates } from '../../redux/modules/countries';
import { PAYMENT_RECEIVER_TYPES } from '../../constants/claim';
import { getCitizenship } from '../countryHelper';
import { bindClaimInsuredPersonData } from './claimApplicationHelpers';
import Currency from '../../components/Currency';

export const handleInputChange = (value, name, setInitState, initState, setValue) => {
  const targetVal = value;
  setInitState({
    ...initState,
    [name]: targetVal,
  });
  setValue(name, targetVal, true);
};

export const passInitialSearchApplicantFieldValues = (
  searchApplicantFilledFields,
  applicantType,
  key,
) => {
  if (searchApplicantFilledFields.status === applicantType && searchApplicantFilledFields[key]) {
    return searchApplicantFilledFields[key];
  }
  return '';
};

export const resolveBackendValidation = (errors, setError, t) => {
  if (errors) {
    forEach(errors, (value, key) => {
      setError(key, 'backendValidation', t(value[0]));
    });
  }
};

export const reduceValidationError = (validationError, status) => validationError
  && reduce(
    validationError,
    (acc, v, k) => {
      acc[`${k}_${status}`] = v;
      return acc;
    },
    {},
  );

export const getApplicantCitizenship = (applicantType, applicantCountry, countries) => {
  if (applicantType !== APPLICANT_TYPES[0].value) return {};
  return getCitizenship(applicantCountry, countries);
};

export const filterTransportManufacturer = (
  transportManufacturer,
  manufacturers,
  setValue,
  setManufacturer,
  dispatch,
) => {
  if (transportManufacturer && manufacturers.length) {
    const selectedManufacturer = isString(transportManufacturer)
      ? find(manufacturers, (o) => toUpper(o.name) === toUpper(transportManufacturer))
      : transportManufacturer;

    setValue('transportManufacturer', selectedManufacturer || null, !!selectedManufacturer);
    setManufacturer(selectedManufacturer || null);
    if (selectedManufacturer) dispatch(getCarManufacturerModels(selectedManufacturer.id));
  }
};

export const changeParticipant = (
  setParticipantIs,
  setValue,
  value,
  register,
  unregister,
) => {
  setParticipantIs(value);
  setValue('participantIs', value);

  if (value === ACCIDENT_PARTICIPANT_TYPES[2].value) {
    register({ name: 'participantCitizenship' });
    register({ name: 'participantBDay' });
  } else {
    unregister(['participantCitizenship', 'participantBDay']);
  }
};

export const filterTransportModel = (
  transportModel,
  manufacturerModels,
  isModelInitialized,
  setValue,
  setModel,
  setIsModelInitialized,
) => {
  if (transportModel && !isEmpty(manufacturerModels) && !isModelInitialized) {
    const filteredTransportModel = typeof transportModel === 'string'
      ? find(manufacturerModels, (o) => toUpper(o.name) === toUpper(transportModel))
      : transportModel;

    setValue('transportModel', filteredTransportModel, !!filteredTransportModel);
    setModel(filteredTransportModel);
    setIsModelInitialized(!!filteredTransportModel);
  }
};

export const changeManufacturer = (
  manufacturer,
  newManufacturer,
  setManufacturer,
  setValue,
  setModel,
  dispatch,
) => {
  if (!manufacturer || newManufacturer.id !== manufacturer.id) {
    setManufacturer(newManufacturer);
    setValue('transportManufacturer', newManufacturer);
    setModel(null);
    setValue('transportModel', null);
    dispatch(getCarManufacturerModels(newManufacturer.id));
  }
};

export const changeAccidentState = (
  setAccAddress,
  accAddress,
  accState,
  setValue,
  dispatch,
) => {
  setAccAddress({
    ...accAddress,
    accState,
    accVillage: null,
  });
  setValue('accState', accState, true);
  setValue('accVillage', null);
  dispatch(getCityVillages(accState.id));
};

export const changeVehicleData = (
  data,
  name,
  setValue,
  setVehicles,
  vehicles,
) => {
  const { id } = data;
  setValue(`${name}_${id}`, data[name], true);
  setVehicles(sortBy([...filter(vehicles, (v) => v.id !== id), data], [(v) => v.id]));
  setValue(
    'vehicles',
    sortBy([...filter(vehicles, (v) => v.id !== id), data], [(v) => v.id]),
    true,
  );
};

export const addVehicle = (
  vehicles,
  getRowSchema,
  setVehicles,
  setSchema,
  getSchema,
  register,
  triggerValidation,
  setValue,
) => {
  const { id: maxId } = maxBy(vehicles, (v) => v.id);

  let isCarsBlockValid = true;
  each(vehicles, ({ id, ...rest }) => {
    const restWithProperKeys = reduce(
      rest,
      (acc, v, k) => {
        acc[`${k}_${id}`] = v;
        return acc;
      },
      {},
    );

    if (!getRowSchema(id).isValidSync(restWithProperKeys)) {
      isCarsBlockValid = false;
      each(rest, (__, key) => {
        triggerValidation({ name: `${key}_${id}` });
      });
    }
  });
  if (!isCarsBlockValid) {
    return;
  }

  const newId = maxId + 1;
  const updatedVehicles = [
    ...vehicles,
    {
      id: newId,
      stateNumber: '',
      manufacturer: null,
      model: null,
    },
  ];

  setVehicles(updatedVehicles);
  setValue('vehicles', updatedVehicles, true);
  setSchema(getSchema(updatedVehicles));

  register({ name: `stateNumber_${newId}` });
  register({ name: `manufacturer_${newId}` });
  register({ name: `model_${newId}` });
};

export const changeCarCountry = (
  carCountry,
  setCarAddress,
  carInitialAddress,
  setValue,
  RAStates,
  dispatch,
  setCarVillages,
) => {
  const isCarInArmenia = carCountry.label === 'ARM';
  setCarAddress({
    ...carInitialAddress,
    isCarInArmenia,
    carCountry,
  });
  setValue('carCountry', carCountry, true);
  setValue('isCarInArmenia', isCarInArmenia);
  setValue('carState', null);
  setValue('carVillage', null);
  if (isCarInArmenia) {
    if (!RAStates.length) dispatch(getStates());
    setCarVillages([]);
  }
};

export const changeCarState = (
  setCarAddress,
  carAddress,
  setValue,
  carState,
  dispatch,
) => {
  setCarAddress({
    ...carAddress,
    carState,
    carVillage: null,
  });
  setValue('carState', carState, true);
  setValue('carVillage', null);
  dispatch(getCarCityVillages(carState.id));
};

export const validateMinValue = (value) => (value ? value.length === 0 || value.length >= 3 : true);

export const getInsurerName = (policy) => {
  if (policy.insuredType === INSURED_TYPES.physical) {
    return `${policy.firstName} ${policy.lastName}`;
  }
  return policy.organizationName ?? policy.insuredOrganizationName;
};

export const checkContractIsDisabled = (startDate, endDate, selectedDate) => {
  if (!selectedDate) return true;
  const accDateParsed = new Date(selectedDate).setHours(0);
  const startDateParsed = new Date(startDate).setHours(0);
  const endDateParsed = new Date(endDate).setHours(0);
  return startDateParsed > accDateParsed || endDateParsed < accDateParsed;
};

export const bindPolicies = (initialData, accDate) => initialData.policies.map((contract) => ({
  ...contract,
  insurer: getInsurerName(contract),
  nonMatching: checkContractIsDisabled(contract.startDate, contract.endDate, accDate),
})) || [];

export const selectPoliciByDefault = (policies, updateContract) => {
  const filteredPolicies = policies.filter((contract) => !contract.nonMatching);
  if (filteredPolicies.length && filteredPolicies.length === 1) {
    updateContract(filteredPolicies[0]);
  }
};

export const filterPaymentReceiverTypes = (initialData) => PAYMENT_RECEIVER_TYPES.filter((type) => {
  const { isOtherApplicant } = initialData;
  let displayedTypes;

  if (isOtherApplicant) {
    displayedTypes = ['insuredPerson', 'applicant', 'other', 'otherLegal'];
  } else {
    displayedTypes = ['insuredPerson', 'other', 'otherLegal'];
  }
  return displayedTypes.includes(type.value);
});

export const sortContractByIsDisabled = (contracts) => contracts.sort(
  (a, b) => a.nonMatching - b.nonMatching,
);

export const handleUpdateDisabledInContracts = (
  date,
  existingContracts,
  setExistingContracts,
  contract,
  updateContract,
  setShowContractNotExistError,
) => {
  if (existingContracts) {
    const updatedContracts = sortContractByIsDisabled(
      existingContracts.map((contractObj) => ({
        ...contractObj,
        nonMatching: checkContractIsDisabled(contractObj.startDate, contractObj.endDate, date),
      })),
    );

    setExistingContracts(updatedContracts);

    if (contract) {
      const contractData = updatedContracts.find(
        ({ policyNumber }) => policyNumber === contract.policyNumber,
      );

      if (contractData.nonMatching) {
        updateContract(null);
      }
    }

    setShowContractNotExistError(
      !updatedContracts.some((updatedContract) => !updatedContract.nonMatching),
    );
  }
};

const getApplicantData = (personalData, insuredPerson, language) => {
  switch (personalData.applicantIs) {
    case 'other':
      return {
        firstName: personalData.applicantFName,
        lastName: personalData.applicantLName,
        passport: personalData.applicantPassNum,
        title: 'claim.conclusion.successTable.applicant',
      };
    case 'otherLegal':
      return {
        name: personalData[`applicantOrganizationName${language.toUpperCase()}`],
        hvhh: personalData.applicantOrganizationHvhh,
        title: 'claim.conclusion.successTable.otherLegalApplicant',
      };
    default:
      return {
        firstName: insuredPerson.firstName,
        lastName: insuredPerson.lastName,
        passport: insuredPerson.passport,
        title: 'claim.conclusion.successTable.applicant',
      };
  }
};

const getPaymentReceiverData = (compensation, applicant, insuredPerson, language) => {
  switch (compensation.paymentReceiverType) {
    case 'applicant':
      return {
        ...applicant,
        title: applicant.hvhh ? 'claim.conclusion.successTable.otherLegalReceiver' : 'claim.conclusion.successTable.receiver',
        transferAccount: compensation.transferAccount,
      };
    case 'other':
      return {
        firstName: compensation.paymentReceiverFirstName,
        lastName: compensation.paymentReceiverLastName,
        transferAccount: compensation.transferAccount,
        title: 'claim.conclusion.successTable.receiver',
      };
    case 'otherLegal':
      return {
        name: compensation[`paymentReceiverName${language.toUpperCase()}`],
        hvhh: compensation.paymentReceiverHvhh,
        transferAccount: compensation.transferAccount,
        title: 'claim.conclusion.successTable.otherLegalReceiver',
      };
    default:
      return {
        firstName: insuredPerson.firstName,
        lastName: insuredPerson.lastName,
        transferAccount: compensation.transferAccount,
        title: 'claim.conclusion.successTable.receiver',
      };
  }
};

const renderPersonData = (person) => {
  if (person.hvhh) {
    return (
      <span>
        {person.name}
        <br />
        {person.hvhh}
      </span>
    );
  }
  return (
    <span>
      {person.firstName}
      {' '}
      {person.lastName}
      <br />
      {person.passport}
    </span>
  );
};

const renderReceiverData = (person) => {
  if (person.hvhh) {
    return (
      <span>
        {person.name}
        {' '}
        {person.hvhh}
        <br />
        {person.transferAccount}
      </span>
    );
  }
  return (
    <span>
      {person.firstName}
      {' '}
      {person.lastName}
      <br />
      {person.transferAccount}
    </span>
  );
};

const getNonImageAttachedFiles = (attachments, translate) => Array.from(new Set(attachments.files.map((item) => translate(`claim.attachments.${humps.camelize(item.type)}`)))).join(', ') || '-';

export const bindClaimConclusionSuccessTableData = (data, translate, language) => {
  const insuredPerson = bindClaimInsuredPersonData(data.personalData);
  const { personalData, accidentInfo, compensation } = data;

  const applicant = getApplicantData(personalData, insuredPerson, language);
  const paymentReceiver = getPaymentReceiverData(compensation, applicant, insuredPerson, language);

  return [
    {
      title: 'claim.conclusion.successTable.dateOfSubmissionOfDocuments',
      data: format(new Date(), DATE_FORMAT_SHORT),
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.contractNumber',
      data: accidentInfo.contract.policyNumber,
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.insuredPerson',
      data: renderPersonData(insuredPerson),
      divider: <hr />,
    },
    {
      title: applicant.title,
      data: renderPersonData(applicant),
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.compensationAmount',
      data: (
        <Currency
          value={accidentInfo.damageAmount}
          currency={accidentInfo.currency.code}
          withSymbol
        />
      ),
      divider: <hr />,
    },
    {
      title: paymentReceiver.title,
      data: renderReceiverData(paymentReceiver),
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.accidentInfo',
      data: <span>{accidentInfo.accidentInformation}</span>,
      divider: <hr />,
    },
    {
      title: 'claim.conclusion.successTable.attachedDocs',
      data: <span>{getNonImageAttachedFiles(data.attachments, translate)}</span>,
    },
  ];
};

export const prepareSearchRequestsPayload = (data) => {
  const preparedData = data;

  if (preparedData.ssn) {
    delete preparedData.pnum;
  } else {
    delete preparedData.ssn;
  }

  if (preparedData.policy) {
    delete preparedData.tpnum;
  } else {
    delete preparedData.policy;
  }

  return preparedData;
};
